import type {
  FunnelAnalyticsReport,
  FunnelAnalyticsStep,
  FunnelAttributionRow,
  FunnelRevenueStep,
  FunnelStatus,
  FunnelVariantComparisonRow,
  PaymentAttempt
} from "@funnel-os/core";

export interface ConsoleFunnel {
  accountId: string;
  funnelId: string;
  displayName?: string;
  createdAt: string;
  status: FunnelStatus;
  previewUrl?: string;
  liveUrl?: string;
  latestDeployAt?: string;
}

export interface ConsoleContact {
  id: string;
  ownerId: string;
  funnelId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  source: string;
  optedIn: boolean;
  isSynthetic: boolean;
  createdAt: string;
}

export interface ConsolePayment {
  id: string;
  orderId: string;
  funnelId: string;
  step: DashboardPaymentStep;
  provider: string;
  providerPaymentRef?: string;
  status: PaymentAttempt["status"];
  amountCents: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConsoleAgentEvent {
  id: string;
  ownerId: string;
  funnelId?: string;
  source: string;
  level: "info" | "warning" | "error";
  type: string;
  message: string;
  metadata: Record<string, string | number | boolean | null>;
  createdAt: string;
}

export type ConsoleAnalytics = FunnelAnalyticsReport;
export type DashboardPaymentStep = "checkout" | "bump" | "upsell" | "downsell" | (string & {});

export interface ConsoleViewInput {
  accountId: string;
  funnels: ConsoleFunnel[];
  contacts?: ConsoleContact[];
  payments?: ConsolePayment[];
  agentEvents?: ConsoleAgentEvent[];
  analyticsByFunnel: Map<string, ConsoleAnalytics>;
}

export interface ConsoleViewModel {
  accountId: string;
  firstRun: boolean;
  summary: {
    liveFunnels: number;
    leadCount: number;
    revenueCents: number;
    revenuePartial: boolean;
    splitTestsRunning: number;
    splitTestsPartial: boolean;
  };
  funnels: FunnelRowModel[];
  contacts: ContactRowModel[];
  payments: PaymentRowModel[];
  agentEvents: ConsoleAgentEvent[];
  analytics: AnalyticsViewModel[];
}

export interface FunnelRowModel extends ConsoleFunnel {
  label: string;
  displayStatus: string;
  leadCount: number;
  revenueLabel: string;
}

export interface ContactRowModel extends ConsoleContact {
  displayName: string;
  sourceLabel: string;
}

export interface PaymentRowModel extends ConsolePayment {
  amountLabel: string;
  testLabel: string;
}

export interface AnalyticsViewModel {
  funnelId: string;
  includeSynthetic: boolean;
  empty: boolean;
  emptyMessage: string;
  revenueLabel: string;
  dropOff: Array<FunnelAnalyticsStep & { dropOffRate: number }>;
  revenue: Array<FunnelRevenueStep & { revenueLabel: string }>;
  attribution: FunnelAttributionRow[];
  variants: Array<FunnelVariantComparisonRow & { revenueLabel: string }>;
}

export function buildConsoleViewModel(input: ConsoleViewInput): ConsoleViewModel {
  const contacts = input.contacts ?? [];
  const payments = input.payments ?? [];
  const agentEvents = input.agentEvents ?? [];
  const analytics = [...input.analyticsByFunnel.values()].map(buildAnalyticsViewModel);
  const realAnalytics = analytics.filter((item) => !item.includeSynthetic);
  const totalRevenue = realAnalytics.reduce((sum, item) => sum + centsFromMoneyLabel(item.revenueLabel), 0);
  const splitTestsRunning = realAnalytics.filter((item) => item.variants.length > 0).length;
  const loadedRealFunnelIds = new Set(realAnalytics.map((item) => item.funnelId));
  const analyticsPartial = loadedRealFunnelIds.size < input.funnels.length;
  const contactsByFunnel = countBy(contacts, (contact) => contact.funnelId);
  const revenueByFunnel = new Map(
    realAnalytics.map((item) => [item.funnelId, item.revenueLabel])
  );
  const noOperatorActivity =
    contacts.length === 0 &&
    payments.length === 0 &&
    agentEvents.length === 0 &&
    analytics.every((item) => item.empty);
  const onlyStarterFunnels =
    input.funnels.length === 0 || input.funnels.every((funnel) => funnel.status === "draft");

  return {
    accountId: input.accountId,
    firstRun: noOperatorActivity && onlyStarterFunnels,
    summary: {
      liveFunnels: input.funnels.filter((funnel) => funnel.status === "live").length,
      leadCount: contacts.length,
      revenueCents: totalRevenue,
      revenuePartial: analyticsPartial,
      splitTestsRunning,
      splitTestsPartial: analyticsPartial
    },
    funnels: input.funnels.map((funnel) => ({
      ...funnel,
      label: funnel.displayName ?? funnel.funnelId,
      displayStatus: displayStatus(funnel.status),
      leadCount: contactsByFunnel.get(funnel.funnelId) ?? 0,
      revenueLabel: revenueByFunnel.get(funnel.funnelId) ?? "Not loaded"
    })),
    contacts: contacts.map((contact) => ({
      ...contact,
      displayName: personDisplayName(contact),
      sourceLabel: contact.source || "Direct"
    })),
    payments: payments.map((payment) => ({
      ...payment,
      amountLabel: formatMoney(payment.amountCents, payment.currency),
      testLabel: "[TEST]"
    })),
    agentEvents,
    analytics
  };
}

export function buildAnalyticsViewModel(report: ConsoleAnalytics): AnalyticsViewModel {
  const revenueCents = report.revenue.reduce((sum, row) => sum + row.revenueAmount, 0);
  const currency = report.revenue[0]?.revenueCurrency ?? report.variants[0]?.revenueCurrency ?? "USD";

  return {
    funnelId: report.funnelId,
    includeSynthetic: report.includeSynthetic,
    empty: report.steps.length === 0 && report.revenue.length === 0 && report.variants.length === 0,
    emptyMessage: "No traffic yet. Run test traffic to populate this view.",
    revenueLabel: formatMoney(revenueCents, currency),
    dropOff: report.steps.map((step) => ({
      ...step,
      dropOffRate: step.visitors === 0 ? 0 : Number((step.dropOffVisitors / step.visitors).toFixed(4))
    })),
    revenue: report.revenue.map((row) => ({
      ...row,
      revenueLabel: formatMoney(row.revenueAmount, row.revenueCurrency)
    })),
    attribution: report.attribution,
    variants: [...report.variants]
      .sort((left, right) => right.revenueAmount - left.revenueAmount || left.variantId.localeCompare(right.variantId))
      .map((row) => ({
        ...row,
        revenueLabel: formatMoney(row.revenueAmount, row.revenueCurrency)
      }))
  };
}

export function formatMoney(amountCents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amountCents / 100);
}

function displayStatus(status: FunnelStatus): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function countBy<T>(items: T[], key: (item: T) => string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const nextKey = key(item);
    counts.set(nextKey, (counts.get(nextKey) ?? 0) + 1);
  }
  return counts;
}

function centsFromMoneyLabel(label: string): number {
  return Math.round(Number(label.replace(/[^0-9.-]/g, "")) * 100);
}

function personDisplayName(person: Pick<ConsoleContact, "email" | "firstName" | "lastName">): string {
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || person.email;
}
